/**
 * Ajustes: `settings/*` editados con formularios (27/10: nada de JSON a la vista; importes en euros,
 * interruptores, listas). El servidor valida con el schema. «Modo avanzado» deja el JSON para casos raros.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { clubSettingsOf } from '@nuvvu/shared';
import { tokens } from '@nuvvu/ui';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, JsonEditor, muted, Notice, str } from '../lib/ui.js';
import { FormEditor } from '../lib/FormEditor.js';

const DOCS = [
  'fees',
  'proPlans',
  'featuredPrices',
  'countries',
  'company',
  'featureFlags',
  'legal',
  'socialPublisher',
  'adServer',
  'environment',
  'announcement',
  'club',
] as const;
type DocName = (typeof DOCS)[number];
const DOC_LABEL: Record<DocName, string> = {
  fees: 'Comisiones y plazos',
  proPlans: 'Planes PRO',
  featuredPrices: 'Destacados y subidas',
  countries: 'Países',
  company: 'Empresa',
  featureFlags: 'Funciones (on/off)',
  legal: 'Textos legales',
  socialPublisher: 'Redes sociales',
  adServer: 'Publicidad',
  environment: 'Entorno y mantenimiento',
  announcement: 'Anuncio a usuarios',
  club: 'Club nuvvu',
};
const toPlain = (v: unknown): unknown =>
  JSON.parse(
    JSON.stringify(v, (_k, x: unknown) =>
      x && typeof x === 'object' && 'toDate' in x
        ? (x as { toDate: () => Date }).toDate().toISOString()
        : x,
    ),
  );
type Doc = Record<string, unknown>;

/** Aviso «Actualizamos nuestros documentos legales» a todos los usuarios (admin-notifyTermsUpdated). */
function TermsNotice(): ReactElement {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = (dry: boolean): void => {
    setMsg(null);
    setErr(null);
    if (
      !dry &&
      !window.confirm(
        `¿Avisar a TODOS los usuarios de que los documentos legales cambian el ${date}?`,
      )
    )
      return;
    call(api.admin.notifyTermsUpdated, { effective_date: date, dry_run: dry })
      .then((r) => {
        setMsg(
          dry ? `Recibirían el aviso: ${r.total} usuarios.` : `Avisados: ${r.notified} usuarios.`,
        );
      })
      .catch((e: unknown) => {
        setErr(errorText(e));
      });
  };
  return (
    <section style={card}>
      <strong>Aviso de cambio de documentos legales</strong>
      <span style={muted}>
        Los textos legales se publican con un despliegue. Después, avisa a los usuarios
        (notificación y correo) con la fecha desde la que se aplican.
      </span>
      <input
        type="date"
        style={field}
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          style={btn(false)}
          onClick={() => {
            run(true);
          }}
        >
          Contar destinatarios
        </button>
        <button
          type="button"
          style={btn(true)}
          onClick={() => {
            run(false);
          }}
        >
          Enviar aviso
        </button>
      </div>
      <Notice text={msg} />
      <Notice text={err} error />
    </section>
  );
}

export function SettingsPage(): ReactElement {
  const params = useParams();
  const name = (DOCS as readonly string[]).includes(params.doc ?? '')
    ? (params.doc as DocName)
    : 'fees';
  const [data, setData] = useState<Doc | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [text, setText] = useState('');
  const [meta, setMeta] = useState<string>('');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    setNotice(null);
    setError(null);
    setAdvanced(false);
    getDoc(doc(db, 'settings', name))
      .then((s) => {
        const d = s.data() ?? {};
        const { updated_at, updated_by, ...rest } = d;
        setMeta(
          `última edición: ${updated_at && typeof updated_at === 'object' && 'toDate' in updated_at ? (updated_at as { toDate: () => Date }).toDate().toLocaleString('es-ES') : '—'} por ${str(updated_by) || '—'}`,
        );
        // 2.1: `settings/club` puede no existir aún: se edita sobre los valores que ya se aplican.
        setData(toPlain(name === 'club' ? { ...clubSettingsOf(rest), ...rest } : rest) as Doc);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setData({});
      });
  }, [name]);
  const switchMode = (): void => {
    if (!advanced) {
      setText(JSON.stringify(data ?? {}, null, 2));
      setAdvanced(true);
      return;
    }
    try {
      setData(JSON.parse(text) as Doc);
      setAdvanced(false);
    } catch (e) {
      setError(`JSON no válido: ${String(e)}`);
    }
  };
  const save = (): void => {
    setError(null);
    setNotice(null);
    let payload: Doc;
    try {
      payload = advanced ? (JSON.parse(text) as Doc) : (data ?? {});
    } catch (e) {
      setError(`JSON no válido: ${String(e)}`);
      return;
    }
    if (!window.confirm(`¿Guardar «${DOC_LABEL[name]}»? Entra en vigor al momento.`)) return;
    call(api.admin.updateSettings, { doc: name, data: payload, ...(reason ? { reason } : {}) })
      .then(() => {
        setNotice('Guardado.');
        setReason('');
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Ajustes</h1>
      <nav style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {DOCS.map((d) => (
          <NavLink
            key={d}
            to={`/ajustes/${d}`}
            style={({ isActive }) => ({
              ...btn(isActive),
              textDecoration: 'none',
              color: isActive ? tokens.color.primaryFg : tokens.color.fg,
            })}
          >
            {DOC_LABEL[d]}
          </NavLink>
        ))}
      </nav>
      <Notice text={notice} />
      <Notice text={error} error />
      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ flex: 1 }}>{DOC_LABEL[name]}</strong>
          <span style={muted}>{meta}</span>
          <button type="button" style={btn(false)} onClick={switchMode}>
            {advanced ? 'Volver al formulario' : 'Modo avanzado'}
          </button>
        </div>
        {data === null ? (
          <p style={muted}>Cargando…</p>
        ) : advanced ? (
          <JsonEditor value={text} onChange={setText} rows={34} />
        ) : (
          <FormEditor value={data} onChange={setData} />
        )}
      </section>
      <section
        style={{
          ...card,
          position: 'sticky',
          bottom: 0,
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          boxShadow: '0 -4px 12px rgba(0,0,0,.06)',
        }}
      >
        <input
          style={field}
          placeholder="Motivo del cambio (queda en la auditoría)"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
        <button type="button" style={btn(true)} onClick={save}>
          Guardar
        </button>
      </section>
      <Notice text={notice} />
      <Notice text={error} error />
      {name === 'legal' && <TermsNotice />}
    </>
  );
}
